import { msg } from "@lingui/core/macro";
import { createMemo, For, Show } from "solid-js";
import { Link } from "@tanstack/solid-router";
import {
  ArrowUpRight,
  Boxes,
  ClipboardCheck,
  Factory,
  Package,
  PackagePlus,
  ScanBarcode,
  ShoppingCart,
  TrendingUp,
  Warehouse,
} from "@fleet/icons";
import {
  cn,
  computeReplenishmentSuggestions,
  getInventoryCountsQuery,
  getLocationsQuery,
  getProductsQuery,
  getPurchaseOrdersQuery,
  getReorderRulesQuery,
  getStockQuantsQuery,
  getWarehousesQuery,
  queryKeys,
} from "@stockpilot/shared";
import { createPowerSyncQuery } from "@stockpilot/shared/solid";
import type {
  InventoryCountRecord,
  LocationRecord,
  ProductRecord,
  PurchaseOrderRecord,
  ReorderRuleRecord,
  StockQuantRecord,
  WarehouseRecord,
} from "@stockpilot/powersync";
import { useSession } from "@/lib/auth";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useI18n } from "@/providers/i18n-provider";
import { useOrgId } from "@/features/inventory/use-org-id";
import { BarChart, type ChartDatum, DonutChart } from "@/components/ui/charts";
import { DataRegionBoundary } from "@/components/ui/data-region-boundary";

/**
 * WMS dashboard (Stockpilot). Replaces the template's list dashboard:
 * shows inventory KPIs (catalog, stock on hand, low stock, pending counts,
 * open purchase orders) and shortcuts to the domain.
 *
 * It is local-first: all queries read from SQLite/PowerSync without suspending,
 * so it renders with the shell without a skeleton.
 */
export function Dashboard() {
  const { t } = useI18n();
  const session = useSession();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const orgId = useOrgId();

  const enabled = () => Boolean(drizzle() && orgId());

  const products = createPowerSyncQuery<ProductRecord>({
    queryKey: () => queryKeys.inventory.products(orgId()),
    queryFn: () => getProductsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled,
  });

  const quants = createPowerSyncQuery<StockQuantRecord>({
    queryKey: () => queryKeys.inventory.quants(orgId()),
    queryFn: () => getStockQuantsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled,
  });

  const rules = createPowerSyncQuery<ReorderRuleRecord>({
    queryKey: () => queryKeys.inventory.reorderRules(orgId()),
    queryFn: () => getReorderRulesQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled,
  });

  const locations = createPowerSyncQuery<LocationRecord>({
    queryKey: () => queryKeys.inventory.locations(orgId()),
    queryFn: () => getLocationsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled,
  });

  const counts = createPowerSyncQuery<InventoryCountRecord>({
    queryKey: () => queryKeys.inventory.counts(orgId()),
    queryFn: () => getInventoryCountsQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled,
  });

  const purchaseOrders = createPowerSyncQuery<PurchaseOrderRecord>({
    queryKey: () => queryKeys.inventory.purchaseOrders(orgId()),
    queryFn: () => getPurchaseOrdersQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled,
  });

  const warehouses = createPowerSyncQuery<WarehouseRecord>({
    queryKey: () => queryKeys.inventory.warehouses(orgId()),
    queryFn: () => getWarehousesQuery(drizzle()!, orgId()),
    db: () => db(),
    enabled,
  });

  const productCount = () => (products.data ?? []).length;

  const onHandUnits = createMemo(() =>
    (quants.data ?? []).reduce((sum, q) => sum + (q.qty ?? 0), 0),
  );

  const lowStockCount = createMemo(() => {
    const locWh = new Map((locations.data ?? []).map((l) => [l.id, l.warehouse_id]));
    const byProduct = new Map<string, number>();
    const byKey = new Map<string, number>();
    for (const q of quants.data ?? []) {
      const pid = q.product_id;
      if (!pid) {
        continue;
      }
      const qty = q.qty ?? 0;
      byProduct.set(pid, (byProduct.get(pid) ?? 0) + qty);
      const wh = locWh.get(q.location_id ?? "") ?? null;
      if (wh) {
        const key = `${pid} ${wh}`;
        byKey.set(key, (byKey.get(key) ?? 0) + qty);
      }
    }
    return computeReplenishmentSuggestions(rules.data ?? [], byKey, byProduct).length;
  });

  const pendingCounts = createMemo(
    () => (counts.data ?? []).filter((c) => c.state !== "done").length,
  );

  const openPurchaseOrders = createMemo(
    () => (purchaseOrders.data ?? []).filter((po) => po.state !== "received").length,
  );

  const firstName = (): string | undefined => session.data?.user.name?.split(" ")[0];

  const warehouseCount = createMemo(
    () => new Set((locations.data ?? []).map((l) => l.warehouse_id).filter(Boolean)).size,
  );

  // ── Analytics: stock units by warehouse (bar chart) ─────────────────────
  const stockByWarehouse = createMemo<ChartDatum[]>(() => {
    const locWh = new Map((locations.data ?? []).map((l) => [l.id, l.warehouse_id]));
    const whName = new Map((warehouses.data ?? []).map((w) => [w.id, w.name]));
    const totals = new Map<string, number>();
    for (const q of quants.data ?? []) {
      const wh = locWh.get(q.location_id ?? "") ?? null;
      if (!wh) {
        continue;
      }
      totals.set(wh, (totals.get(wh) ?? 0) + (q.qty ?? 0));
    }
    return [...totals.entries()]
      .map(([wh, qty]) => ({
        label: whName.get(wh) ?? t(msg`Unknown warehouse`),
        value: qty,
        display: String(qty),
        colorClass: "text-primary",
      }))
      .toSorted((a, b) => b.value - a.value);
  });

  // ── Analytics: stock health composition (donut) ─────────────────────────
  const stockHealth = createMemo(() => {
    const byProduct = new Map<string, number>();
    for (const q of quants.data ?? []) {
      const pid = q.product_id;
      if (!pid) {
        continue;
      }
      byProduct.set(pid, (byProduct.get(pid) ?? 0) + (q.qty ?? 0));
    }
    const total = productCount();
    const low = lowStockCount();
    let out = 0;
    for (const p of products.data ?? []) {
      if ((byProduct.get(p.id) ?? 0) <= 0) {
        out += 1;
      }
    }
    const healthy = Math.max(0, total - low - out);
    return { healthy, low, out };
  });

  type KpiTone = "accent" | "warning" | "danger" | "muted";
  const kpis = createMemo(() => [
    {
      to: "/products" as const,
      label: t(msg`Products`),
      hint: t(msg`In catalog`),
      value: productCount(),
      icon: Package,
      tone: "accent" as KpiTone,
    },
    {
      to: "/stock" as const,
      label: t(msg`Units on hand`),
      hint: t(msg`Across all locations`),
      value: onHandUnits(),
      icon: Boxes,
      tone: "accent" as KpiTone,
    },
    {
      to: "/replenishment" as const,
      label: t(msg`Low stock`),
      hint: lowStockCount() > 0 ? t(msg`Needs reorder`) : t(msg`All healthy`),
      value: lowStockCount(),
      icon: TrendingUp,
      tone: (lowStockCount() > 0 ? "warning" : "muted") as KpiTone,
    },
    {
      to: "/counts" as const,
      label: t(msg`Pending counts`),
      hint: pendingCounts() > 0 ? t(msg`Awaiting reconciliation`) : t(msg`Up to date`),
      value: pendingCounts(),
      icon: ClipboardCheck,
      tone: (pendingCounts() > 0 ? "warning" : "muted") as KpiTone,
    },
    {
      to: "/purchases" as const,
      label: t(msg`Open purchase orders`),
      hint: t(msg`In transit or draft`),
      value: openPurchaseOrders(),
      icon: ShoppingCart,
      tone: "accent" as KpiTone,
    },
    {
      to: "/warehouses" as const,
      label: t(msg`Warehouses`),
      hint: t(msg`Active sites`),
      value: warehouseCount(),
      icon: Warehouse,
      tone: "accent" as KpiTone,
    },
  ]);

  const quickActions = createMemo(() => [
    { to: "/products" as const, label: t(msg`Add product`), icon: PackagePlus },
    { to: "/purchases" as const, label: t(msg`New purchase`), icon: ShoppingCart },
    { to: "/counts" as const, label: t(msg`Start count`), icon: ClipboardCheck },
    { to: "/manufacturing" as const, label: t(msg`Manufacturing`), icon: Factory },
  ]);

  const toneClasses: Record<KpiTone, { chip: string; ring: string }> = {
    accent: { chip: "bg-primary/10 text-primary", ring: "group-hover:border-primary/40" },
    warning: { chip: "bg-warning/15 text-warning", ring: "border-warning/40" },
    danger: { chip: "bg-danger/15 text-danger", ring: "border-danger/40" },
    muted: { chip: "bg-success/10 text-success", ring: "group-hover:border-success/40" },
  };

  return (
    <div
      class="flex-1 min-h-0 overflow-y-auto p-6 md:p-10 [scrollbar-gutter:stable]"
      style={{
        "overflow-anchor": "none",
        "will-change": "scroll-position",
      }}
    >
      <div class="mx-auto max-w-7xl space-y-8">
        {/* Hero / command-center banner */}
        <header class="relative overflow-hidden rounded-2xl border border-border/60 bg-card/60 p-6 md:p-8">
          <div
            class="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/15 blur-3xl"
            aria-hidden="true"
          />
          <div class="relative flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div class="space-y-3">
              <div class="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
                <Warehouse class="h-3.5 w-3.5" />
                {t(msg`Warehouse control`)}
              </div>
              <h1 class="text-4xl font-extrabold tracking-tighter md:text-5xl">Stockpilot</h1>
              <Show
                when={session.isAuthenticated()}
                fallback={
                  <p class="max-w-md text-sm text-muted-foreground">
                    {t(
                      msg`You are in visitor mode. Sign in to sync your inventory across devices.`,
                    )}
                  </p>
                }
              >
                <p class="text-sm text-muted-foreground">
                  {(() => {
                    const name = firstName() ?? t(msg`User`);
                    return t(msg`Welcome back, ${name}. Here is your inventory at a glance.`);
                  })()}
                </p>
              </Show>
            </div>
            <Link
              to="/products"
              class="inline-flex shrink-0 items-center gap-2 self-start rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-transform hover:scale-[1.02] active:scale-95"
            >
              <ScanBarcode class="h-4 w-4" />
              {t(msg`Manage catalog`)}
            </Link>
          </div>
        </header>

        {/* Quick actions */}
        <section class="flex flex-wrap gap-2" data-testid="dashboard-quick-actions">
          <For each={quickActions()}>
            {(action) => (
              <Link
                to={action.to}
                class="inline-flex items-center gap-2 rounded-lg border border-border bg-card/40 px-3.5 py-2 text-sm font-medium text-foreground/80 transition-[color,background-color,border-color,transform] duration-150 ease-out will-change-transform hover:border-primary/40 hover:bg-card hover:text-foreground active:scale-95"
              >
                <action.icon class="h-4 w-4 text-primary" />
                {action.label}
              </Link>
            )}
          </For>
        </section>

        {/* KPI grid */}
        <section
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          data-testid="dashboard-kpis"
        >
          <For each={kpis()}>
            {(kpi, i) => {
              const tone = toneClasses[kpi.tone];
              return (
                <Link
                  to={kpi.to}
                  style={{ "--row-index": i() }}
                  class={cn(
                    "list-row-in group relative flex items-start gap-4 overflow-hidden rounded-xl border border-border bg-card/60 p-5",
                    "transition-[transform,box-shadow,background-color,border-color] duration-200 ease-out will-change-transform",
                    "hover:-translate-y-0.5 hover:bg-card hover:shadow-[var(--surface-shadow)] active:translate-y-0 active:scale-[0.99]",
                    tone.ring,
                  )}
                  data-testid="dashboard-kpi"
                >
                  <span
                    class={cn(
                      "flex h-12 w-12 shrink-0 items-center justify-center rounded-lg",
                      tone.chip,
                    )}
                  >
                    <kpi.icon class="h-6 w-6" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="text-3xl font-bold tabular-nums leading-none">{kpi.value}</p>
                    <p class="mt-1.5 text-sm font-medium text-foreground/90 truncate">
                      {kpi.label}
                    </p>
                    <p class="text-xs text-muted-foreground truncate">{kpi.hint}</p>
                  </div>
                  <ArrowUpRight class="h-4 w-4 shrink-0 text-muted-foreground/40 transition-colors group-hover:text-primary" />
                </Link>
              );
            }}
          </For>
        </section>

        {/* Analytics — theme-aware, dependency-free charts */}
        <DataRegionBoundary>
          <section class="grid grid-cols-1 gap-4 lg:grid-cols-5" data-testid="dashboard-analytics">
            {/* Stock by warehouse (bar) */}
            <div class="rounded-xl border border-border bg-card/60 p-5 lg:col-span-3">
              <div class="mb-4 flex items-center justify-between gap-3">
                <h2 class="text-sm font-semibold tracking-tight text-foreground/90">
                  {t(msg`Stock by warehouse`)}
                </h2>
                <Link
                  to="/stock"
                  class="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  {t(msg`View stock`)}
                  <ArrowUpRight class="h-3.5 w-3.5" />
                </Link>
              </div>
              <Show
                when={stockByWarehouse().length > 0}
                fallback={
                  <p class="py-6 text-center text-sm text-muted-foreground">
                    {t(msg`No stock recorded yet.`)}
                  </p>
                }
              >
                <BarChart
                  ariaLabel={t(msg`Units on hand by warehouse`)}
                  data={stockByWarehouse()}
                  max={6}
                />
              </Show>
            </div>

            {/* Stock health (donut) */}
            <div class="rounded-xl border border-border bg-card/60 p-5 lg:col-span-2">
              <div class="mb-4 flex items-center justify-between gap-3">
                <h2 class="text-sm font-semibold tracking-tight text-foreground/90">
                  {t(msg`Stock health`)}
                </h2>
                <Link
                  to="/replenishment"
                  class="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  {t(msg`Replenish`)}
                  <ArrowUpRight class="h-3.5 w-3.5" />
                </Link>
              </div>
              <Show
                when={productCount() > 0}
                fallback={
                  <p class="py-6 text-center text-sm text-muted-foreground">
                    {t(msg`Add products to see their health.`)}
                  </p>
                }
              >
                <DonutChart
                  ariaLabel={t(msg`Stock health by product`)}
                  centerValue={String(productCount())}
                  centerCaption={t(msg`products`)}
                  segments={[
                    {
                      label: t(msg`Healthy`),
                      value: stockHealth().healthy,
                      color: "hsl(var(--success))",
                    },
                    {
                      label: t(msg`Low stock`),
                      value: stockHealth().low,
                      color: "hsl(var(--warning))",
                    },
                    {
                      label: t(msg`Out of stock`),
                      value: stockHealth().out,
                      color: "hsl(var(--danger))",
                    },
                  ]}
                />
              </Show>
            </div>
          </section>
        </DataRegionBoundary>
      </div>
    </div>
  );
}
